import { BtnBasicCopy } from '@components/buttons';
import { BaseDialog, BtnCancel } from '@components/dialog';
import { categoryLabel } from './file-thumbnail';
import { IcoClipboard, IcoPaperclip } from '@components/icons';
import { showModalForm } from '@components/modal-form';
import { AttachmentItem } from '@course/components/attachments/attachment-item';
import { isAudio, isImage, isPDF, isVideo } from 'shared/media';
import { ManagedFile } from './state';
import { UsagePanel } from './usage-panel';

type Props = {
  file: ManagedFile;
  onClose(): void;
};

/**
 * The minimal shape PreviewContent needs to render. ManagedFile satisfies it,
 * but so does any ad-hoc object (e.g. the Transcoded Originals detail modal
 * previewing a legacy original vs its CMAF transcode side by side).
 */
export type PreviewFile = Pick<
  ManagedFile,
  'id' | 'name' | 'type' | 'publicUrl' | 'posterUrl' | 'isVideoBucket'
>;

function PreviewModal({ file, onClose }: Props) {
  return (
    <BaseDialog onClose={onClose} contentWidth blurBg={false} compact>
      <section class="w-4xl max-w-full max-h-[90vh] bg-white rounded-lg overflow-hidden flex flex-col">
        <header class="px-5 py-3 border-b border-gray-200 flex items-center justify-between gap-4 shrink-0">
          <div class="min-w-0 flex-1">
            <h2 class="text-sm font-semibold text-gray-900 truncate">{file.name}</h2>
            <div class="flex items-center gap-2 text-xs text-gray-500">
              <span>{categoryLabel(file.type)}</span>
              <span class="text-gray-300">·</span>
              <code class="font-mono truncate">{file.id}</code>
              <BtnBasicCopy
                value={file.id}
                iconOnly
                class="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                icon={<IcoClipboard class="size-3.5" />}
                copiedText="Copied file ID"
              />
            </div>
          </div>
          <BtnCancel onClick={onClose}>Close</BtnCancel>
        </header>
        <div class="flex-1 min-h-0 overflow-auto bg-gray-50">
          <div class="p-4 flex items-center justify-center">
            <PreviewContent file={file} />
          </div>
          <UsagePanel fileId={file.id} />
        </div>
      </section>
    </BaseDialog>
  );
}

export function PreviewContent({ file }: { file: PreviewFile }) {
  if (isPDF(file.type)) {
    return (
      <iframe
        src={`/pdf.html?pdf=${encodeURIComponent(file.publicUrl)}`}
        class="w-full h-[78vh] bg-gray-800 rounded-md"
        title={file.name}
      />
    );
  }

  if (isImage(file.type) || isVideo(file.type) || isAudio(file.type)) {
    return (
      <AttachmentItem
        attachment={{
          id: file.id,
          name: file.name,
          type: file.type,
          url: file.publicUrl,
          poster: file.posterUrl,
          isVideoBucket: file.isVideoBucket,
        }}
        fullSize
      />
    );
  }

  return (
    <a
      href={file.publicUrl}
      target="_blank"
      rel="noopener noreferrer"
      class="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-800 px-5 py-3 rounded-lg"
    >
      <IcoPaperclip class="size-4" />
      Open file in a new tab
    </a>
  );
}

export async function showMediaPreview(file: ManagedFile): Promise<void> {
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
  await showModalForm<void>(({ resolve }) => (
    <PreviewModal file={file} onClose={() => resolve(undefined)} />
  ));
  if (opener?.isConnected) {
    opener.focus();
  }
}
